(function () {
  'use strict';

  var SESSION_KEY = 'hbcCheckInSession';
  var CLOUDFLARE_TICKET_KEY = 'hbcCheckInCloudflareTicket';
  var CLOUDFLARE_TICKET_EXPIRY_KEY = 'hbcCheckInCloudflareTicketExpiry';
  var CLOUDFLARE_URL_KEY = 'hbcCheckInCloudflareUrl';
  var STAFF_NAME_KEY = 'hbcCheckInStaffName';
  var COOLDOWN_MS = 1800;
  var REQUEST_TIMEOUT_MS = 20000;
  var CHECK_IN_FALLBACK_TIMEOUT_MS = 7000;
  var AUTH_REQUEST_TIMEOUT_MS = 15000;
  var SCANNER_LIBRARY_TIMEOUT_MS = 6000;
  var SCANNER_LIBRARY_URLS = [
    'https://cdn.jsdelivr.net/npm/html5-qrcode@2.3.8/html5-qrcode.min.js',
    'https://unpkg.com/html5-qrcode@2.3.8/html5-qrcode.min.js'
  ];
  var PHOTO_CACHE_NAME = 'hbc-private-participant-photos-v1';
  var PHOTO_CACHE_MAX_AGE_MS = 36 * 60 * 60 * 1000;
  var MASTER_WALKIN_QR = 'HBC-MASTER-WALKIN:DOOR';

  function formatUkDate(value) {
    var match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return match ? [match[3], match[2], match[1]].join('/') : (value || 'Not supplied');
  }

  function HbcCheckIn(root) {
    this.root = root;
    this.apiUrl = root.dataset.apiUrl;
    this.readerId = root.dataset.readerId;
    this.sessionToken = sessionStorage.getItem(SESSION_KEY) || '';
    this.staffName = sessionStorage.getItem(STAFF_NAME_KEY) || '';
    this.scanner = null;
    this.scanning = false;
    this.cameraStarting = false;
    this.cameraStartPromise = null;
    this.cameraPreferred = false;
    this.scannerLibraryPromise = null;
    this.processing = false;
    this.authenticating = false;
    this.lastToken = '';
    this.lastScanAt = 0;
    this.cloudflareUrl = sessionStorage.getItem(CLOUDFLARE_URL_KEY) || '';
    this.cloudflareTicket = sessionStorage.getItem(CLOUDFLARE_TICKET_KEY) || '';
    this.cloudflareTicketExpiresAt = Number(
      sessionStorage.getItem(CLOUDFLARE_TICKET_EXPIRY_KEY) || 0
    );
    this.cloudflareTicketPromise = null;
    this.photoCache = new Map();
    this.bind();
    this.restoreSession();
  }

  HbcCheckIn.prototype.bind = function () {
    var self = this;
    this.root.querySelector('[data-checkin-login-form]').addEventListener('submit', function (event) {
      event.preventDefault();
      self.login(new FormData(event.currentTarget));
    });
    this.root.querySelector('[data-start-camera]').addEventListener('click', function () {
      self.startCamera();
    });
    this.root.querySelector('[data-manual-form]').addEventListener('submit', function (event) {
      event.preventDefault();
      var value = new FormData(event.currentTarget).get('checkInToken');
      self.processToken(value);
    });
    this.root.querySelector('[data-scan-next]').addEventListener('click', function () {
      self.resetScanner(true, true);
    });
    this.root.querySelector('[data-retry-scan]').addEventListener('click', function () {
      self.resetScanner(true, true);
    });
    this.root.querySelector('[data-checkin-logout]').addEventListener('click', function () {
      self.logout();
    });
    this.root.querySelector('[data-walkin-cancel]').addEventListener('click', function () {
      self.closeWalkInPanel(true);
    });
    this.root.querySelector('[data-walkin-form]').addEventListener('submit', function (event) {
      event.preventDefault();
      self.submitAnonymousWalkIns(new FormData(event.currentTarget));
    });
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) self.stopCamera();
    });
    window.addEventListener('pagehide', function () { self.stopCamera(); });
  };

  HbcCheckIn.prototype.request = function (payload, timeoutMs) {
    var controller = typeof window.AbortController === 'function' ?
      new window.AbortController() :
      null;
    var timeout = controller ? window.setTimeout(function () {
      controller.abort();
    }, timeoutMs || REQUEST_TIMEOUT_MS) : null;
    var options = {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(payload),
      redirect: 'follow',
      credentials: 'omit'
    };
    if (payload && payload.type === 'cloudflare-checkin-reconcile') options.keepalive = true;
    if (controller) options.signal = controller.signal;

    return fetch(this.apiUrl, options).then(function (response) {
      if (!response.ok) throw new Error('The check-in service is unavailable.');
      return response.json();
    }).catch(function (error) {
      if (error && error.name === 'AbortError') {
        throw new Error(
          'The check-in service took too long to respond. Try the same QR code again; it will not check the customer in twice.'
        );
      }
      throw error;
    }).finally(function () {
      if (timeout) window.clearTimeout(timeout);
    });
  };

  HbcCheckIn.prototype.prepareCloudflareLive = function (force) {
    var self = this;
    if (!this.sessionToken) return Promise.reject(new Error('no-session'));
    if (!force && this.cloudflareTicket &&
        this.cloudflareTicketExpiresAt > Date.now() + 60000) {
      return Promise.resolve(true);
    }
    if (this.cloudflareTicketPromise) return this.cloudflareTicketPromise;
    this.cloudflareTicketPromise = this.request({
      type: 'scanner-live-ticket',
      sessionToken: this.sessionToken
    }, AUTH_REQUEST_TIMEOUT_MS).then(function (data) {
      if (!data || data.ok !== true || !data.ticket || !data.workerUrl) {
        throw new Error('cloudflare-ticket-unavailable');
      }
      self.cloudflareUrl = String(data.workerUrl).replace(/\/+$/, '');
      self.cloudflareTicket = data.ticket;
      self.cloudflareTicketExpiresAt = Number(data.expiresAt) || 0;
      sessionStorage.setItem(CLOUDFLARE_URL_KEY, self.cloudflareUrl);
      sessionStorage.setItem(CLOUDFLARE_TICKET_KEY, self.cloudflareTicket);
      sessionStorage.setItem(
        CLOUDFLARE_TICKET_EXPIRY_KEY,
        String(self.cloudflareTicketExpiresAt)
      );
      return true;
    }).finally(function () {
      self.cloudflareTicketPromise = null;
    });
    return this.cloudflareTicketPromise;
  };

  HbcCheckIn.prototype.cloudflareCheckIn = function (token) {
    var self = this;
    return this.prepareCloudflareLive(false).then(function () {
      var controller = typeof window.AbortController === 'function' ?
        new window.AbortController() : null;
      var timeout = controller ? window.setTimeout(function () {
        controller.abort();
      }, 3500) : null;
      return fetch(self.cloudflareUrl + '/v1/live/check-in', {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({
          ticket: self.cloudflareTicket,
          customerQrToken: String(token || '')
            .replace(/^HBC-(?:CHECKIN|CUSTOMER):/i, '')
        }),
        credentials: 'omit',
        signal: controller ? controller.signal : undefined
      }).then(function (response) {
        if (!response.ok) throw new Error('cloudflare-http-' + response.status);
        return response.json();
      }).then(function (data) {
        if (!data || data.fallback === true || data.status === 'worker-auth-failed') {
          throw new Error('cloudflare-fallback');
        }
        return data;
      }).finally(function () {
        if (timeout) window.clearTimeout(timeout);
      });
    });
  };

  HbcCheckIn.prototype.appsScriptCheckIn = function (token, demoMode, timeoutMs) {
    return this.request({
      type: 'check-in',
      checkInToken: token,
      sessionToken: this.sessionToken,
      demoMode: demoMode
    }, timeoutMs || REQUEST_TIMEOUT_MS);
  };

  HbcCheckIn.prototype.appsScriptCheckInWithRetry = function (token, demoMode) {
    var self = this;
    var timeoutMs = demoMode ? REQUEST_TIMEOUT_MS : CHECK_IN_FALLBACK_TIMEOUT_MS;
    return this.appsScriptCheckIn(token, demoMode, timeoutMs).then(function (data) {
      if (!data || ['busy', 'temporary-unavailable'].indexOf(String(data.status || '')) === -1) {
        return data;
      }
      return new Promise(function (resolve) {
        window.setTimeout(resolve, 750);
      }).then(function () {
        return self.appsScriptCheckIn(token, demoMode, timeoutMs);
      });
    });
  };

  HbcCheckIn.prototype.primaryCheckIn = function (token, demoMode) {
    var self = this;
    if (demoMode) return this.appsScriptCheckInWithRetry(token, true);
    return this.cloudflareCheckIn(token).catch(function () {
      return self.appsScriptCheckInWithRetry(token, false).then(function (data) {
        if (data) data.source = 'apps-script-fallback';
        return data;
      });
    });
  };

  HbcCheckIn.prototype.reconcileCloudflareResult = function (data) {
    if (!data || data.source !== 'cloudflare' || data.ok !== true ||
        !Array.isArray(data.bookingIds) || !data.bookingIds.length) return;
    this.request({
      type: 'cloudflare-checkin-reconcile',
      sessionToken: this.sessionToken,
      bookingIds: data.bookingIds
    }, 15000).catch(function () {});
  };

  HbcCheckIn.prototype.restoreSession = function () {
    var self = this;
    if (!this.sessionToken) return this.showLogin();
    // Always validate the Apps Script session. A cached Cloudflare ticket can
    // outlive a repaired or revoked staff session and previously made the UI
    // appear signed in while fallback scans and anonymous check-ins failed.
    var checkSession = function () {
      return self.request({
        type: 'staff-session',
        sessionToken: self.sessionToken
      }, AUTH_REQUEST_TIMEOUT_MS).then(function (data) {
        if (!data || data.ok !== true) throw new Error('expired');
        return data;
      });
    };

    checkSession().catch(function () {
      return new Promise(function (resolve) {
        window.setTimeout(resolve, 750);
      }).then(checkSession);
    }).then(function (data) {
      if (!data || data.ok !== true) throw new Error('expired');
      self.showWorkspace(data.staffName);
      self.prepareCloudflareLive().then(function () {
        self.schedulePhotoPreload();
      }).catch(function () {});
    }).catch(function () {
      self.clearSession();
      self.showLogin();
    });
  };

  HbcCheckIn.prototype.login = function (formData) {
    if (this.authenticating) return;
    var self = this;
    var form = this.root.querySelector('[data-checkin-login-form]');
    var button = form.querySelector('button[type="submit"]');
    var buttonLabel = button.querySelector('span') || button;
    var error = this.root.querySelector('[data-login-error]');
    var status = this.root.querySelector('[data-login-status]');
    this.authenticating = true;
    this.root.classList.add('is-authenticating');
    form.setAttribute('aria-busy', 'true');
    error.textContent = '';
    if (status) status.textContent = 'Verifying account and preparing scanner...';
    button.disabled = true;
    buttonLabel.textContent = 'Signing in...';
    var loginPayload = {
      type: 'staff-login',
      username: String(formData.get('username') || '').trim(),
      accessCode: String(formData.get('accessCode') || '')
    };
    var requestLogin = function () {
      return self.request(loginPayload, AUTH_REQUEST_TIMEOUT_MS);
    };
    requestLogin().catch(function () {
      return new Promise(function (resolve) {
        window.setTimeout(resolve, 500);
      }).then(requestLogin);
    }).then(function (data) {
      if (!data || data.ok !== true || !data.sessionToken) {
        throw new Error(data && data.message ? data.message : 'Sign in failed.');
      }
      self.sessionToken = data.sessionToken;
      sessionStorage.setItem(SESSION_KEY, self.sessionToken);
      sessionStorage.setItem(STAFF_NAME_KEY, data.staffName || 'hbc-admin');
      form.reset();
      self.showWorkspace(data.staffName);
      self.prepareCloudflareLive(true).then(function () {
        self.schedulePhotoPreload();
      }).catch(function () {});
    }).catch(function (loginError) {
      error.textContent = loginError.message || 'Sign in failed. Try again.';
      if (status) status.textContent = 'Sign in was not completed';
    }).finally(function () {
      self.authenticating = false;
      self.root.classList.remove('is-authenticating');
      form.removeAttribute('aria-busy');
      button.disabled = false;
      buttonLabel.textContent = 'Sign in securely';
    });
  };

  HbcCheckIn.prototype.showLogin = function () {
    this.processing = false;
    this.authenticating = false;
    this.cameraPreferred = false;
    this.root.classList.remove('is-authenticating', 'is-checking', 'is-camera-active',
      'is-result-success', 'is-result-failure', 'is-result-member', 'is-result-gold', 'is-result-system-error');
    document.body.classList.remove('hbc-checkin-signal-green', 'hbc-checkin-signal-red',
      'hbc-checkin-signal-blue', 'hbc-checkin-signal-gold', 'hbc-checkin-signal-yellow');
    this.stopCamera();
    this.root.querySelector('[data-checkin-login]').hidden = false;
    this.root.querySelector('[data-checkin-workspace]').hidden = true;
  };

  HbcCheckIn.prototype.showWorkspace = function (staffName) {
    this.root.classList.remove('is-authenticating', 'is-signing-out');
    this.staffName = String(staffName || 'Staff').toLowerCase();
    this.root.dataset.staffAccount = this.staffName;
    this.root.querySelector('[data-checkin-login]').hidden = true;
    this.root.querySelector('[data-checkin-workspace]').hidden = false;
    this.root.querySelector('[data-staff-name]').textContent = staffName || 'Staff';
    this.resetScanner(false, false);
  };

  HbcCheckIn.prototype.moveIntoView = function (element, position) {
    if (!element) return;
    var top = element.getBoundingClientRect().top + window.pageYOffset;
    var offset = position === 'camera' ?
      Math.max(10, (window.innerHeight - Math.min(element.offsetHeight, window.innerHeight * 0.72)) / 2) :
      10;
    window.requestAnimationFrame(function () {
      window.scrollTo({ top: Math.max(0, top - offset), behavior: 'auto' });
    });
  };

  HbcCheckIn.prototype.startCamera = function () {
    var self = this;
    var help = this.root.querySelector('[data-camera-help]');
    var reader = this.root.querySelector('#' + this.readerId);
    var startButton = this.root.querySelector('[data-start-camera]');
    help.textContent = '';

    if (typeof window.Html5Qrcode !== 'function') {
      help.textContent = 'Loading camera scanner...';
      return this.loadScannerLibrary().then(function () {
        help.textContent = '';
        return self.startCamera();
      }).catch(function () {
        help.textContent = 'Camera scanner could not load. Use manual entry below.';
        self.setStatus('Camera unavailable');
      });
    }
    if (this.scanning || this.cameraStarting || this.processing) return;

    this.cameraPreferred = true;
    this.cameraStarting = true;
    this.root.classList.add('is-camera-active');
    reader.hidden = false;
    startButton.hidden = true;
    this.setStatus('Starting camera...');
    this.scanner = this.scanner || new window.Html5Qrcode(
      this.readerId,
      window.Html5QrcodeSupportedFormats ? {
        formatsToSupport: [window.Html5QrcodeSupportedFormats.QR_CODE]
      } : undefined
    );

    this.cameraStartPromise = this.scanner.start(
      { facingMode: 'environment' },
      { fps: 12, qrbox: function (width, height) {
        var size = Math.floor(Math.min(width, height) * 0.78);
        return { width: size, height: size };
      } },
      function (decodedText) { self.processToken(decodedText); },
      function () {}
    ).then(function () {
      self.cameraStarting = false;
      self.scanning = true;
      self.setStatus('Ready to scan');
      window.setTimeout(function () {
        self.moveIntoView(reader, 'camera');
      }, 120);
    }).catch(function () {
      self.cameraStarting = false;
      self.scanning = false;
      self.root.classList.remove('is-camera-active');
      reader.hidden = true;
      startButton.hidden = false;
      help.textContent = 'Camera access was unavailable. Check permission settings or use manual entry.';
      self.setStatus('Camera unavailable');
    });
    return this.cameraStartPromise;
  };

  HbcCheckIn.prototype.loadScannerLibrary = function () {
    var self = this;
    if (typeof window.Html5Qrcode === 'function') return Promise.resolve();
    if (this.scannerLibraryPromise) return this.scannerLibraryPromise;

    var loadFrom = function (index) {
      if (typeof window.Html5Qrcode === 'function') return Promise.resolve();
      if (index >= SCANNER_LIBRARY_URLS.length) {
        return Promise.reject(new Error('scanner-library-unavailable'));
      }
      return new Promise(function (resolve, reject) {
        var settled = false;
        var script = document.createElement('script');
        var timer = window.setTimeout(function () {
          if (settled) return;
          settled = true;
          script.remove();
          reject(new Error('scanner-library-timeout'));
        }, SCANNER_LIBRARY_TIMEOUT_MS);
        var finish = function (error) {
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          if (error) {
            script.remove();
            reject(error);
          } else {
            resolve();
          }
        };
        script.src = SCANNER_LIBRARY_URLS[index];
        script.async = true;
        script.onload = function () {
          finish(typeof window.Html5Qrcode === 'function' ? null :
            new Error('scanner-library-invalid'));
        };
        script.onerror = function () {
          finish(new Error('scanner-library-unavailable'));
        };
        document.head.appendChild(script);
      }).catch(function () {
        return loadFrom(index + 1);
      });
    };

    this.scannerLibraryPromise = loadFrom(0).catch(function (error) {
      // A temporary CDN/network failure must not poison the scanner for the
      // rest of the staff session. The next button press gets a fresh attempt.
      self.scannerLibraryPromise = null;
      throw error;
    });
    return this.scannerLibraryPromise;
  };

  HbcCheckIn.prototype.stopCamera = function () {
    var self = this;
    if (this.cameraStarting && this.cameraStartPromise) {
      return this.cameraStartPromise.finally(function () {
        self.cameraStartPromise = null;
      }).then(function () {
        return self.stopCamera();
      });
    }
    if (!this.scanner || !this.scanning) return Promise.resolve();
    this.scanning = false;
    return this.scanner.stop().catch(function () {}).then(function () {
      self.cameraStartPromise = null;
      var reader = self.root.querySelector('#' + self.readerId);
      if (reader) reader.hidden = true;
    });
  };

  HbcCheckIn.prototype.normaliseToken = function (value) {
    var token = String(value || '').trim();
    if (token.length > 180) return '';
    if (/^HBC-(?:CHECKIN|CUSTOMER):[A-Za-z0-9_-]{8,128}$/i.test(token)) return token;
    if (/^[A-Za-z0-9_-]{8,128}$/.test(token)) return 'HBC-CHECKIN:' + token;
    return '';
  };

  HbcCheckIn.prototype.processToken = function (value) {
    var self = this;
    if (String(value || '').trim().toUpperCase() === MASTER_WALKIN_QR) {
      if (this.processing) return;
      this.processing = true;
      return this.stopCamera().finally(function () {
        return self.openWalkInPanel();
      }).finally(function () {
        self.processing = false;
      });
    }
    var token = this.normaliseToken(value);
    var now = Date.now();
    if (!token) {
      return this.stopCamera().finally(function () {
        self.showLocalError('Invalid QR code. No matching booking was found.');
      });
    }
    if (this.processing || (token === this.lastToken && now - this.lastScanAt < COOLDOWN_MS)) return;

    this.processing = true;
    this.root.classList.remove('is-camera-active');
    this.root.classList.add('is-checking');
    this.lastToken = token;
    this.lastScanAt = now;
    this.setStatus('Checking booking...');
    this.stopCamera().finally(function () {
      self.primaryCheckIn(
        token,
        self.root.querySelector('[data-demo-mode]').checked
      ).then(function (data) {
        if (data && /session.*expired|session.*missing/i.test(data.message || '')) {
          self.clearSession();
          self.showLogin();
          self.root.querySelector('[data-login-error]').textContent =
            'Your staff session expired. Sign in again.';
          return;
        }
        var result = data || {
          ok: false,
          status: 'error',
          message: 'The check-in service returned an invalid response.'
        };
        self.showResult(result);
        if (result.source !== 'cloudflare') {
          self.compareCloudflareShadow(token, result);
        }
      }).catch(function (error) {
        self.showResult({
          ok: false,
          status: 'system-error',
          message: error.message || 'Could not reach the check-in service. Try again.'
        });
      }).finally(function () {
        self.processing = false;
        self.root.classList.remove('is-checking');
      });
    });
  };

  HbcCheckIn.prototype.showLocalError = function (message) {
    this.showResult({ ok: false, status: 'not-found', message: message });
  };

  HbcCheckIn.prototype.compareCloudflareShadow = function (token, result) {
    var rawToken = String(token || '')
      .replace(/^HBC-(?:CHECKIN|CUSTOMER):/i, '')
      .trim();
    if (!/^[a-f0-9]{64}$/i.test(rawToken) || !this.sessionToken) return;
    this.request({
      type: 'cloudflare-shadow-compare',
      sessionToken: this.sessionToken,
      customerQrToken: rawToken,
      appsStatus: String(result.status || '')
    }, 8000).catch(function () {});
  };

  HbcCheckIn.prototype.showResult = function (data) {
    this.root.classList.remove('is-checking', 'is-camera-active');
    var panel = this.root.querySelector('[data-checkin-result]');
    var ready = this.root.querySelector('[data-checkin-ready]');
    var status = String(data.status || (data.ok ? 'checked-in' : 'error'));
    var successful = data.ok === true &&
      (status === 'checked-in' ||
       status === 'checked-in-renewal-due' ||
       status === 'anonymous-checked-in' ||
       status === 'demo-success');
    var systemError = status === 'error' || status === 'system-error' || status === 'busy' ||
      status === 'worker-auth-failed' || status === 'maintenance' ||
      status === 'service-unavailable' || status === 'timeout' ||
      status === 'price-missing' || status === 'token-conflict';
    var memberResult = successful && data.isMember === true;
    var goldResult = successful && Boolean(data.loyaltyRewardRedeemed || data.loyaltyRewardBooking);
    var titles = {
      'checked-in': 'Customer checked in',
      'checked-in-renewal-due': 'CHECKED IN - RENEW MEMBERSHIP TODAY',
      'demo-success': 'Scanner test passed',
      'already-checked-in': 'QR already scanned',
      'wrong-date': 'Wrong booking date',
      'wrong-time': 'Wrong class time',
      'price-missing': 'Payment amount missing',
      'token-conflict': 'Duplicate QR record',
      'busy': 'Scanner busy',
      'system-error': 'System issue - try again',
      'worker-auth-failed': 'Scanner connection expired',
      'maintenance': 'Scanner maintenance',
      'service-unavailable': 'Service temporarily unavailable',
      'timeout': 'Scanner timed out',
      'anonymous-checked-in': 'Anonymous attendees checked in',
      'cancelled': 'Booking cancelled',
      'invalid-status': 'Booking unavailable',
      'not-booked': 'Not booked for this class',
      'membership-expired': 'Membership expired',
      'qr-revoked': 'QR code revoked',
      'waiver-required': 'Online waiver required',
      'not-found': 'Invalid QR code',
      'error': 'Check-in failed'
    };
    if (status === 'wrong-time') {
      var lateTiming = data.timing === 'late' || /window closed|too late/i.test(data.message || '');
      titles['wrong-time'] = lateTiming ?
        'Check-in window closed' :
        'Too early to check in';
    }

    panel.className = 'hbc-checkin__result ' +
      (goldResult ? 'is-gold' : (memberResult ? 'is-member' :
        (successful ? 'is-success' : (systemError ? 'is-system-error' : 'is-error'))));
    this.root.classList.remove('is-result-success', 'is-result-failure', 'is-result-member', 'is-result-gold', 'is-result-system-error');
    this.root.classList.add(goldResult ? 'is-result-gold' :
      (memberResult ? 'is-result-member' : (successful ? 'is-result-success' :
        (systemError ? 'is-result-system-error' : 'is-result-failure'))));
    document.body.classList.remove('hbc-checkin-signal-green', 'hbc-checkin-signal-red',
      'hbc-checkin-signal-blue', 'hbc-checkin-signal-gold', 'hbc-checkin-signal-yellow');
    document.body.classList.add(goldResult ? 'hbc-checkin-signal-gold' :
      (memberResult ? 'hbc-checkin-signal-blue' :
        (successful ? 'hbc-checkin-signal-green' :
          (systemError ? 'hbc-checkin-signal-yellow' : 'hbc-checkin-signal-red'))));
    panel.querySelector('[data-result-label]').textContent =
      status === 'demo-success' ?
        'DEMONSTRATION ONLY' :
        (successful ? 'CHECK-IN COMPLETE' : (systemError ? 'SYSTEM CHECK REQUIRED' : 'NOT CHECKED IN'));
    panel.querySelector('[data-result-title]').textContent = titles[status] || 'Check-in result';
    panel.querySelector('[data-result-payment-label]').textContent =
      status === 'demo-success' ?
        'Test amount - do not collect' :
        successful ?
        'Collect payment' :
        (status === 'already-checked-in' ? 'Recorded payment amount' : 'Do not collect yet');
    panel.querySelector('[data-result-price]').textContent =
      Number.isFinite(Number(data.price)) ? '£' + Number(data.price).toFixed(2).replace('.00', '') : 'Check booking';
    if (data.isMember) {
      panel.querySelector('[data-result-payment-label]').textContent = 'Membership';
      panel.querySelector('[data-result-price]').textContent = 'MEMBER - NO PAYMENT';
    }
    if (status === 'anonymous-checked-in') {
      panel.querySelector('[data-result-payment-label]').textContent = 'Attendance added';
      panel.querySelector('[data-result-price]').textContent = 'NO PAYMENT RECORD';
    }
    var stamp = panel.querySelector('[data-result-stamp]');
    if (stamp) {
      var paid = Math.max(0, Math.min(10, Number(data.loyaltyPaidAttendancesInCycle || 0)));
      var visit = data.loyaltyRewardRedeemed || data.loyaltyRewardBooking ? 11 : paid;
      stamp.textContent = data.isMember ? 'MEMBER' :
        (data.loyaltyEnabled === true ?
          (data.loyaltyEligible ? (successful ? visit + '/11' : paid + '/10 STAMPS') : 'NOT ELIGIBLE') :
          'CHECK AT ENTRY');
    }
    panel.classList.toggle('is-loyalty-reward', Boolean(data.loyaltyRewardRedeemed || data.loyaltyRewardBooking));
    var loyalty = panel.querySelector('[data-result-loyalty]');
    if (loyalty) {
      var loyaltyText = panel.querySelector('[data-result-loyalty-text]');
      var loyaltyDetail = panel.querySelector('[data-result-loyalty-detail]');
      loyalty.hidden = !data.loyaltyEnabled && !data.isMember;
      loyalty.classList.toggle('is-reward', Boolean(data.loyaltyRewardAvailable || data.loyaltyRewardBooking || data.loyaltyRewardRedeemed));
      loyaltyText.textContent = data.isMember ? 'MEMBER - LOYALTY NOT APPLICABLE' :
        (data.loyaltyDisplayText || 'PAYG LOYALTY UPDATE PENDING');
      loyaltyDetail.textContent = data.isMember ? 'Membership classes do not collect PAYG loyalty stamps.' :
        (data.loyaltyRewardRedeemed ? 'The free class was redeemed. A new cycle starts with the next eligible paid visit.' :
          'Progress changes only after a successful eligible QR check-in.');
    }
    panel.querySelector('[data-result-name]').textContent =
      [data.firstName, data.lastName].filter(Boolean).join(' ') || 'Not supplied';
    panel.querySelector('[data-result-class]').textContent = data.className || 'Not supplied';
    panel.querySelector('[data-result-date]').textContent = formatUkDate(data.bookingDate);
    panel.querySelector('[data-result-time]').textContent = data.bookingTime || 'Not supplied';
    panel.querySelector('[data-result-id]').textContent = data.bookingId || 'Not supplied';
    panel.querySelector('[data-result-message]').textContent =
      data.message || 'Unable to complete check-in.';
    var medical = panel.querySelector('[data-result-medical]');
    if (medical) {
      var showMedical = this.staffName === 'hbc-emily' && String(data.medicalInfo || '').trim();
      medical.hidden = !showMedical;
      panel.querySelector('[data-result-medical-text]').textContent =
        showMedical ? String(data.medicalInfo || 'None declared') : '';
    }
    panel.querySelector('[data-retry-scan]').hidden =
      !systemError;
    ready.hidden = true;
    panel.hidden = false;
    panel.focus({ preventScroll: true });
    window.scrollTo({ top: Math.max(0, this.root.getBoundingClientRect().top + window.pageYOffset), behavior: 'auto' });
    this.prepareParticipantPhoto(data);
    this.announce(status === 'demo-success' ?
      'Scanner test passed. No payment or attendance was recorded.' :
      panel.querySelector('[data-result-title]').textContent + '. ' +
        panel.querySelector('[data-result-price]').textContent + ' to pay.');
  };

  HbcCheckIn.prototype.prepareParticipantPhoto = function (data) {
    var self = this;
    var wrap = this.root.querySelector('[data-result-photo-wrap]');
    if (!wrap) return;
    if (this.staffName === 'hbc-emily') {
      wrap.hidden = true;
      return;
    }
    if (
      String(data.status || '').indexOf('demo-') === 0 ||
      /^HBC-SCANNER-/i.test(data.bookingId || '')
    ) {
      wrap.hidden = true;
      return;
    }
    var image = this.root.querySelector('[data-result-photo]');
    var placeholder = this.root.querySelector('[data-result-photo-placeholder]');
    image.removeAttribute('src');
    image.hidden = true;
    placeholder.hidden = false;
    placeholder.textContent = [data.firstName, data.lastName]
      .filter(Boolean)
      .map(function (name) { return name.charAt(0).toUpperCase(); })
      .join('').slice(0, 2) || '?';
    wrap.hidden = false;

    if (!data.bookingId && !data.customerId) {
      return;
    }
    var cacheKey = String(data.customerId || data.bookingId || '');
    var cached = cacheKey ? this.photoCache.get(cacheKey) : '';
    if (cached) {
      image.onload = function () { placeholder.hidden = true; image.hidden = false; };
      image.src = cached;
      return;
    }
    this.loadParticipantPhoto(data).then(function (photoUrl) {
      if (!photoUrl) return;
      image.onload = function () {
        placeholder.hidden = true;
        image.hidden = false;
      };
      image.onerror = function () {};
      image.alt = 'Identification photograph for ' +
        ([data.firstName, data.lastName].filter(Boolean).join(' ') || 'this participant');
      image.src = photoUrl;
      if (cacheKey) self.photoCache.set(cacheKey, photoUrl);
    }).catch(function () {});
  };

  HbcCheckIn.prototype.photoCacheRequest = function (customerId) {
    return new Request(window.location.origin + '/__hbc-private-photo-cache__/' +
      encodeURIComponent(customerId), { method: 'GET' });
  };

  HbcCheckIn.prototype.readBrowserPhoto = function (customerId, expectedVersion) {
    var self = this;
    if (!customerId || !window.caches) return Promise.resolve('');
    return window.caches.open(PHOTO_CACHE_NAME).then(function (cache) {
      return cache.match(self.photoCacheRequest(customerId));
    }).then(function (response) {
      if (!response) return '';
      var cachedAt = Number(response.headers.get('x-hbc-cached-at') || 0);
      var cachedVersion = String(response.headers.get('x-hbc-photo-version') || '');
      if (!cachedAt || Date.now() - cachedAt > PHOTO_CACHE_MAX_AGE_MS ||
          (expectedVersion && cachedVersion !== String(expectedVersion))) {
        return window.caches.open(PHOTO_CACHE_NAME).then(function (cache) {
          return cache.delete(self.photoCacheRequest(customerId));
        }).then(function () { return ''; });
      }
      return response.blob().then(function (blob) {
        if (!/^image\//i.test(blob.type || '')) return '';
        return URL.createObjectURL(blob);
      });
    }).catch(function () { return ''; });
  };

  HbcCheckIn.prototype.writeBrowserPhoto = function (customerId, blob, version) {
    var self = this;
    if (!customerId || !window.caches || !blob || !/^image\//i.test(blob.type || '')) {
      return Promise.resolve();
    }
    var headers = new Headers({
      'content-type': blob.type,
      'x-hbc-cached-at': String(Date.now()),
      'x-hbc-photo-version': String(version || '')
    });
    return window.caches.open(PHOTO_CACHE_NAME).then(function (cache) {
      return cache.put(self.photoCacheRequest(customerId), new Response(blob, { headers: headers }));
    }).catch(function () {});
  };

  HbcCheckIn.prototype.fetchCloudflarePhoto = function (customerId) {
    var self = this;
    if (!customerId) return Promise.reject(new Error('photo-not-migrated'));
    return this.prepareCloudflareLive(false).then(function () {
      return fetch(self.cloudflareUrl + '/v1/live/photo', {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({ ticket: self.cloudflareTicket, customerId: customerId }),
        credentials: 'omit'
      });
    }).then(function (response) {
      if (!response.ok || !/^image\//i.test(response.headers.get('content-type') || '')) {
        throw new Error('photo-not-migrated');
      }
      var version = response.headers.get('x-hbc-photo-version') || '';
      return response.blob().then(function (blob) {
        return self.writeBrowserPhoto(customerId, blob, version).then(function () {
          return URL.createObjectURL(blob);
        });
      });
    });
  };

  HbcCheckIn.prototype.fetchAppsScriptPhoto = function (data) {
    return this.request({
      type: 'participant-photo',
      sessionToken: this.sessionToken,
      bookingId: data.bookingId || '',
      customerId: data.customerId || ''
    }, 12000).then(function (response) {
      return response && response.ok === true ? String(response.photoDataUrl || '') : '';
    });
  };

  HbcCheckIn.prototype.loadParticipantPhoto = function (data) {
    var self = this;
    var customerId = String(data.customerId || '');
    return this.readBrowserPhoto(customerId).then(function (cached) {
      if (cached) return cached;
      return self.fetchCloudflarePhoto(customerId).catch(function () {
        return self.fetchAppsScriptPhoto(data);
      });
    });
  };

  HbcCheckIn.prototype.schedulePhotoPreload = function () {
    var self = this;
    var start = function () {
      self.purgeExpiredPhotoCache().then(function () {
        return self.preloadTodayPhotos();
      }).catch(function () {});
    };
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(start, { timeout: 2500 });
    } else {
      window.setTimeout(start, 600);
    }
  };

  HbcCheckIn.prototype.purgeExpiredPhotoCache = function () {
    if (!window.caches) return Promise.resolve();
    return window.caches.open(PHOTO_CACHE_NAME).then(function (cache) {
      return cache.keys().then(function (keys) {
        return Promise.all(keys.map(function (key) {
          return cache.match(key).then(function (response) {
            var cachedAt = response ? Number(response.headers.get('x-hbc-cached-at') || 0) : 0;
            return !cachedAt || Date.now() - cachedAt > PHOTO_CACHE_MAX_AGE_MS ?
              cache.delete(key) : false;
          });
        }));
      });
    }).catch(function () {});
  };

  HbcCheckIn.prototype.preloadTodayPhotos = function () {
    var self = this;
    if (!this.cloudflareUrl || !this.cloudflareTicket) return Promise.resolve();
    return fetch(this.cloudflareUrl + '/v1/live/photo-manifest', {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify({ ticket: this.cloudflareTicket }),
      credentials: 'omit'
    }).then(function (response) {
      if (!response.ok) throw new Error('manifest-unavailable');
      return response.json();
    }).then(function (manifest) {
      var photos = Array.isArray(manifest.photos) ? manifest.photos.slice(0, 80) : [];
      var position = 0;
      function next() {
        if (position >= photos.length) return Promise.resolve();
        var photo = photos[position++];
        var customerId = String(photo.customerId || '');
        return self.readBrowserPhoto(customerId, photo.version).then(function (cached) {
          if (cached) {
            URL.revokeObjectURL(cached);
            return null;
          }
          return self.fetchCloudflarePhoto(customerId).then(function (url) {
            if (url) URL.revokeObjectURL(url);
          }).catch(function () {});
        }).then(next);
      }
      return Promise.all([next(), next(), next()]);
    });
  };

  HbcCheckIn.prototype.resetScanner = function (clearDuplicate, restartCamera) {
    this.root.classList.remove('is-checking', 'is-camera-active', 'is-result-success',
      'is-result-failure', 'is-result-member', 'is-result-gold', 'is-result-system-error');
    document.body.classList.remove('hbc-checkin-signal-green', 'hbc-checkin-signal-red',
      'hbc-checkin-signal-blue', 'hbc-checkin-signal-gold', 'hbc-checkin-signal-yellow');
    this.root.querySelector('[data-walkin-panel]').hidden = true;
    this.root.querySelector('[data-checkin-result]').hidden = true;
    this.root.querySelector('[data-checkin-ready]').hidden = false;
    this.root.querySelector('[data-manual-form]').reset();
    this.root.querySelector('[data-camera-help]').textContent = '';
    this.root.querySelector('[data-start-camera]').hidden = false;
    if (clearDuplicate) {
      this.lastToken = '';
      this.lastScanAt = 0;
    }
    this.setStatus('Ready to scan');
    if (restartCamera && this.cameraPreferred) {
      this.startCamera();
    }
  };

  HbcCheckIn.prototype.setStatus = function (message) {
    this.root.querySelector('[data-scan-status]').textContent = message;
  };

  HbcCheckIn.prototype.openWalkInPanel = function () {
    var self = this;
    var ready = this.root.querySelector('[data-checkin-ready]');
    var panel = this.root.querySelector('[data-walkin-panel]');
    var select = panel.querySelector('[data-walkin-session]');
    var error = panel.querySelector('[data-walkin-error]');
    ready.hidden = true;
    this.root.querySelector('[data-checkin-result]').hidden = true;
    panel.hidden = false;
    select.disabled = true;
    select.innerHTML = '<option>Loading today\'s classes...</option>';
    error.textContent = '';
    this.setStatus('Preparing anonymous check-in...');
    return this.request({
      type: 'scanner-walkin-sessions',
      sessionToken: this.sessionToken
    }, 10000).then(function (data) {
      if (!data || data.ok !== true || !Array.isArray(data.sessions) || !data.sessions.length) {
        throw new Error('No classes are available today.');
      }
      select.innerHTML = '';
      data.sessions.forEach(function (session) {
        var option = document.createElement('option');
        option.value = session.id;
        option.textContent = session.time + ' - ' + session.className;
        select.appendChild(option);
      });
      select.disabled = false;
      self.setStatus('Enter anonymous attendance');
      panel.querySelector('[name="quantity"]').focus();
    }).catch(function (loadError) {
      error.textContent = loadError.message || 'Classes could not be loaded.';
      self.setStatus('System issue');
    });
  };

  HbcCheckIn.prototype.closeWalkInPanel = function (restartCamera) {
    this.root.querySelector('[data-walkin-panel]').hidden = true;
    this.root.querySelector('[data-checkin-ready]').hidden = false;
    this.setStatus('Ready to scan');
    if (restartCamera && this.cameraPreferred) this.startCamera();
  };

  HbcCheckIn.prototype.submitAnonymousWalkIns = function (formData) {
    if (this.processing) return;
    var self = this;
    var form = this.root.querySelector('[data-walkin-form]');
    var button = form.querySelector('[data-walkin-submit]');
    var error = form.querySelector('[data-walkin-error]');
    this.processing = true;
    button.disabled = true;
    button.textContent = 'Adding attendees...';
    error.textContent = '';
    this.setStatus('Adding and checking in...');
    this.request({
      type: 'scanner-anonymous-walk-ins',
      sessionToken: this.sessionToken,
      sessionId: String(formData.get('sessionId') || ''),
      quantity: Number(formData.get('quantity') || 0)
    }, 20000).then(function (data) {
      if (!data || data.ok !== true) {
        throw new Error(data && data.message ? data.message : 'Anonymous attendance was not added.');
      }
      self.root.querySelector('[data-walkin-panel]').hidden = true;
      self.showResult({
        ok: true,
        status: 'anonymous-checked-in',
        firstName: String(data.quantity || ''),
        lastName: Number(data.quantity) === 1 ? 'anonymous attendee' : 'anonymous attendees',
        className: data.className,
        bookingDate: data.bookingDate,
        bookingTime: data.bookingTime,
        bookingId: Array.isArray(data.bookingIds) ? data.bookingIds.join(', ') : '',
        price: 0,
        message: data.message
      });
    }).catch(function (submitError) {
      error.textContent = submitError.message || 'Anonymous attendance was not added.';
      self.setStatus('System issue - nothing added');
    }).finally(function () {
      self.processing = false;
      button.disabled = false;
      button.textContent = 'Add & check in';
    });
  };

  HbcCheckIn.prototype.logout = function () {
    var self = this;
    if (this.root.classList.contains('is-signing-out')) return;
    var token = this.sessionToken;
    this.root.classList.add('is-signing-out');
    this.root.classList.remove('is-checking', 'is-camera-active', 'is-result-success',
      'is-result-failure', 'is-result-member', 'is-result-gold');
    document.body.classList.remove('hbc-checkin-signal-green', 'hbc-checkin-signal-red',
      'hbc-checkin-signal-blue', 'hbc-checkin-signal-gold');
    this.photoCache.forEach(function (url) {
      if (/^blob:/i.test(String(url || ''))) URL.revokeObjectURL(url);
    });
    this.photoCache.clear();
    if (window.caches) window.caches.delete(PHOTO_CACHE_NAME).catch(function () {});
    this.clearSession();
    this.showLogin();
    var loginStatus = this.root.querySelector('[data-login-status]');
    if (loginStatus) loginStatus.textContent = 'Signed out securely';
    this.stopCamera();
    if (token) {
      this.request({
        type: 'staff-logout',
        sessionToken: token
      }, 2500).catch(function () {});
    }
    window.setTimeout(function () {
      self.root.classList.remove('is-signing-out');
    }, 350);
  };

  HbcCheckIn.prototype.clearSession = function () {
    this.sessionToken = '';
    this.cloudflareUrl = '';
    this.cloudflareTicket = '';
    this.cloudflareTicketExpiresAt = 0;
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(CLOUDFLARE_URL_KEY);
    sessionStorage.removeItem(CLOUDFLARE_TICKET_KEY);
    sessionStorage.removeItem(CLOUDFLARE_TICKET_EXPIRY_KEY);
    sessionStorage.removeItem(STAFF_NAME_KEY);
  };

  HbcCheckIn.prototype.announce = function (message) {
    var announcer = this.root.querySelector('[data-checkin-announcer]');
    announcer.textContent = '';
    window.setTimeout(function () { announcer.textContent = message; }, 20);
  };

  function initialise(scope) {
    var roots = [];
    if (scope.matches && scope.matches('[data-hbc-checkin]')) roots.push(scope);
    roots = roots.concat(Array.from(scope.querySelectorAll('[data-hbc-checkin]')));
    roots.forEach(function (root) {
      if (root.dataset.checkinInitialised) return;
      root.dataset.checkinInitialised = 'true';
      new HbcCheckIn(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initialise(document); });
  } else {
    initialise(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initialise(event.target);
  });
})();
